import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Sparkles, Gift } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls = "w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base";

export default function AuthPage() {
  const [params] = useSearchParams();
  const refCode = (params.get("ref") || "").toUpperCase();
  const [mode, setMode] = useState(refCode ? "register" : "login");
  const [form, setForm] = useState({ parent_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async () => {
    setError("");
    if (!form.email || !form.password || (mode === "register" && !form.parent_name)) {
      setError("Fill in all the fields first.");
      return;
    }
    setBusy(true);
    try {
      const payload = mode === "register" ? { ...form, referral_code: refCode } : { email: form.email, password: form.password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      login(data.token, data.user);
      navigate(data.user.onboarded ? "/" : "/onboarding");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-3">
          <img src="/mascot.png" alt="StudySpark mascot" className="w-24 h-24 mx-auto rounded-full border-4 border-black shadow-[4px_4px_0px_rgba(0,0,0,1)] bg-white object-cover" />
          <h1 className="font-heading text-4xl font-semibold flex items-center justify-center gap-2">
            <Sparkles size={28} className="text-[#FFB800]" /> StudySpark
          </h1>
          <p className="text-gray-600 text-sm">The ADHD-savvy AI study coach that keeps your teen on track — and gets them to that A.</p>
        </div>

        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 space-y-4" data-testid="auth-card">
          {refCode && mode === "register" && (
            <div className="flex items-center gap-2 bg-[#F2C6DE] border-2 border-black rounded-xl px-3 py-2.5 text-sm font-bold" data-testid="referral-banner">
              <Gift size={16} className="shrink-0" /> You've been invited by a friend — welcome!
            </div>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => { setMode("login"); setError(""); }}
              data-testid="auth-tab-login"
              className={`flex-1 py-2 rounded-full border-2 border-black font-bold text-sm transition-all ${mode === "login" ? "bg-[#FFE066] shadow-[2px_2px_0px_rgba(0,0,0,1)]" : "bg-white text-gray-500"}`}
            >
              Log in
            </button>
            <button
              onClick={() => { setMode("register"); setError(""); }}
              data-testid="auth-tab-register"
              className={`flex-1 py-2 rounded-full border-2 border-black font-bold text-sm transition-all ${mode === "register" ? "bg-[#FFE066] shadow-[2px_2px_0px_rgba(0,0,0,1)]" : "bg-white text-gray-500"}`}
            >
              Create account
            </button>
          </div>

          {mode === "register" && (
            <input className={inputCls} placeholder="Your name (parent)" value={form.parent_name}
              onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="auth-parent-name-input" />
          )}
          <input className={inputCls} type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" />
          <input className={inputCls} type="password" placeholder={mode === "register" ? "Password (min 6 characters)" : "Password"}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="auth-password-input" />

          {error && <p className="text-sm font-bold text-[#FF6B6B]" data-testid="auth-error">{error}</p>}

          {mode === "login" && (
            <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-sm font-bold text-gray-500 hover:text-black transition-colors underline">
              Forgot your password?
            </Link>
          )}

          <button onClick={submit} disabled={busy} data-testid="auth-submit-button"
            className="w-full bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3 disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 size={18} className="animate-spin" />}
            {mode === "login" ? "Log in" : "Start free 7-day trial"}
          </button>
          {mode === "register" && <p className="text-xs text-gray-500 text-center">7 days free, then A$19/month automatically — cancel anytime.</p>}
        </div>
      </div>
    </div>
  );
}
